/*
  BFCM — behavior of the bfcm-* landing page sections.
  Custom elements, so they also (re)initialize on their own when a section is re-rendered in the theme editor.
*/
(() => {
  if (window.__bfcmLp) return;
  window.__bfcmLp = true;

  const root = document.documentElement;

  /*
    Header: measures the announcement bar and the logo / button bar and exposes their heights as CSS variables
    (--bfcm-ann-h: the sticky offset that lets the announcement scroll away; --bfcm-bar-h: how far the hero is pulled
    up under the translucent bar). Adds .is-scrolled once the page is scrolled, which gives the bar a solid background.
  */
  if (!customElements.get('bfcm-header')) {
    customElements.define(
      'bfcm-header',
      class extends HTMLElement {
        connectedCallback() {
          this.announcement = this.querySelector('[data-bfcm-announcement]');
          this.bar = this.querySelector('[data-bfcm-bar]');
          this.measure = this.measure.bind(this);
          this.onScroll = this.onScroll.bind(this);

          this.resizeObserver = new ResizeObserver(this.measure);
          this.resizeObserver.observe(this);
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.measure();
          this.onScroll();
        }

        disconnectedCallback() {
          this.resizeObserver?.disconnect();
          window.removeEventListener('scroll', this.onScroll);
        }

        /*
          Exact (fractional) heights: offsetHeight rounds, e.g. a 34.39px announcement gives 34px, which leaves a hairline
          of it at the top of the screen once the header sticks. The announcement offset is rounded up for the same reason.
        */
        measure() {
          const announcement = this.announcement ? Math.ceil(this.announcement.getBoundingClientRect().height) : 0;
          root.style.setProperty('--bfcm-ann-h', `${announcement}px`);
          root.style.setProperty('--bfcm-bar-h', `${this.bar ? this.bar.getBoundingClientRect().height : 0}px`);
        }

        onScroll() {
          const threshold = (this.announcement ? this.announcement.offsetHeight : 0) + 10;
          this.classList.toggle('is-scrolled', window.scrollY > threshold);
        }
      }
    );
  }

  /*
    Countdown (snippets/bfcm-countdown.liquid), set up by the header ([data-bfcm-countdown-mode]):
      daily   restarts every 24 hours: counts down to the next midnight in the store time zone (data-bfcm-timezone,
              e.g. -0500), but never past "Sale ends" (data-bfcm-sale-end) when that is set
      end     counts down to "Sale ends" once
    A countdown with its own data-end counts down to that instead. Fills its 4 [data-bfcm-unit] boxes (days, hours,
    minutes, seconds), or, without boxes, its own text as DD:HH:MM:SS (sticky bar). Stops at 00 once the end has passed.
  */
  if (!customElements.get('bfcm-countdown')) {
    const DAY = 86400000;

    /* "-0500" → -300 (minutes east of UTC) */
    const parseOffset = (value) => {
      const match = /^([+-])(\d{2}):?(\d{2})$/.exec(value || '');
      return match ? (match[1] === '-' ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10)) : 0;
    };

    customElements.define(
      'bfcm-countdown',
      class extends HTMLElement {
        connectedCallback() {
          const config = document.querySelector('[data-bfcm-countdown-mode]')?.dataset || {};
          this.end = Date.parse(this.dataset.end || config.bfcmSaleEnd || '');
          this.daily = !this.dataset.end && config.bfcmCountdownMode === 'daily';
          this.offset = parseOffset(config.bfcmTimezone) * 60000;
          if (!this.daily && Number.isNaN(this.end)) return;

          this.units = this.querySelectorAll('[data-bfcm-unit]');
          this.tick = this.tick.bind(this);
          this.tick();
          this.timer = setInterval(this.tick, 1000);
        }

        disconnectedCallback() {
          clearInterval(this.timer);
        }

        target(now) {
          if (!this.daily) return this.end;

          /* next midnight of the store's wall clock (exactly at midnight: now, so it shows 00 for that second) */
          const midnight = Math.ceil((now + this.offset) / DAY) * DAY - this.offset;
          return Number.isNaN(this.end) ? midnight : Math.min(midnight, this.end);
        }

        tick() {
          const now = Date.now();
          const left = Math.max(0, Math.floor((this.target(now) - now) / 1000));
          const parts = [Math.floor(left / 86400), Math.floor(left / 3600) % 24, Math.floor(left / 60) % 60, left % 60].map(
            (value) => String(value).padStart(2, '0')
          );

          if (this.units.length) this.units.forEach((unit, index) => (unit.textContent = parts[index] ?? ''));
          else this.textContent = parts.join(':');

          /* the 24-hour countdown starts over at midnight; it only stops once the sale itself has ended */
          if (left === 0 && (!this.daily || now >= this.end)) {
            clearInterval(this.timer);
            this.classList.add('is-expired');
          }
        }
      }
    );
  }

  /*
    Sticky mobile bar of the header: shown once the hero button ([data-bfcm-sticky-trigger]) has been scrolled past,
    hidden while a [data-bfcm-sticky-hide] element (the closing section) is in view. Checked on scroll rather than
    with an IntersectionObserver, which stays silent when a jump (anchor link, restored scroll position) skips over
    the button.
  */
  if (!customElements.get('bfcm-sticky-bar')) {
    customElements.define(
      'bfcm-sticky-bar',
      class extends HTMLElement {
        connectedCallback() {
          this.update = this.update.bind(this);
          this.onScroll = () => {
            if (!this.frame) this.frame = requestAnimationFrame(this.update);
          };

          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          cancelAnimationFrame(this.frame);
        }

        update() {
          this.frame = null;

          const trigger = document.querySelector('[data-bfcm-sticky-trigger]');
          const passed = !trigger || trigger.getBoundingClientRect().bottom < 0;
          const hidden = [...document.querySelectorAll('[data-bfcm-sticky-hide]')].some((element) => {
            const rect = element.getBoundingClientRect();
            return rect.bottom > 0 && rect.top < window.innerHeight;
          });

          this.classList.toggle('is-visible', passed && !hidden);
        }
      }
    );
  }

  /*
    Free gift progress (sections/bfcm-gift.liquid): rendered with the cart of the page load, then kept in sync with
    /cart.js: right away (in case the page came from a cache), when the page is shown from the back/forward cache (e.g.
    after adding a bag on a product page and going back) and when the tab becomes visible again.
      data-threshold      the cart total that unlocks the gift, in cents
      data-money-format   the shop money format, e.g. ${{amount}}
  */
  if (!customElements.get('bfcm-gift-progress')) {
    const formatMoney = (cents, format) => {
      const value = (cents / 100).toFixed(2);
      const [whole, decimals] = value.split('.');
      const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const amount = decimals === '00' ? withCommas : `${withCommas}.${decimals}`;

      return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) =>
        key === 'amount_no_decimals' ? withCommas : amount
      );
    };

    customElements.define(
      'bfcm-gift-progress',
      class extends HTMLElement {
        connectedCallback() {
          this.onPageShow = (event) => event.persisted && this.refresh();
          this.onVisibility = () => document.visibilityState === 'visible' && this.refresh();

          window.addEventListener('pageshow', this.onPageShow);
          document.addEventListener('visibilitychange', this.onVisibility);
          this.refresh();
        }

        disconnectedCallback() {
          window.removeEventListener('pageshow', this.onPageShow);
          document.removeEventListener('visibilitychange', this.onVisibility);
        }

        async refresh() {
          try {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
            const cart = await response.json();
            this.render(cart.total_price);
          } catch (error) {
            /* keep what was rendered */
          }
        }

        render(total) {
          const threshold = parseInt(this.dataset.threshold, 10);
          if (!threshold) return;

          const remaining = Math.max(0, threshold - total);
          const fill = this.querySelector('[data-bfcm-fill]');
          const amount = this.querySelector('[data-bfcm-amount]');

          if (fill) fill.style.width = `${Math.min(100, Math.floor((total / threshold) * 100))}%`;
          if (amount) amount.textContent = formatMoney(remaining, this.dataset.moneyFormat);
          this.classList.toggle('is-done', remaining === 0);
        }
      }
    );
  }

  /* "Sign Up for VIP Access" buttons link to #bfcm-signup: scroll to the form and focus its e-mail field */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#bfcm-signup"]');
    if (!link) return;

    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const input = target.querySelector('input[type="email"]');
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 500);
  });

  /* Sets the value of an input the way a user would, so React-driven forms (Klaviyo) pick it up */
  const setNativeValue = (input, value) => {
    const descriptor =
      Object.getOwnPropertyDescriptor(Object.getPrototypeOf(input), 'value') ||
      Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

    if (descriptor?.set) descriptor.set.call(input, value);
    else input.value = value;

    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /*
    Sign-up form (snippets/bfcm-signup-form.liquid): the styled form the visitor sees. On submit it fills in and submits
    the real Klaviyo embed form, which is rendered off-screen inside the same element, and listens to Klaviyo's
    "klaviyoForms" events to know when the subscription went through. If Klaviyo never answers, the real embed form is
    shown instead so the visitor can still sign up.
      data-klaviyo-form   the Klaviyo form ID (the "SF2Bv6" of klaviyo-form-SF2Bv6)
      data-timeout        ms to wait for Klaviyo's confirmation before falling back (default 8000)
  */
  if (!customElements.get('bfcm-signup')) {
    customElements.define(
      'bfcm-signup',
      class extends HTMLElement {
        connectedCallback() {
          this.form = this.querySelector('form');
          if (!this.form) return;

          this.host = this.querySelector('[data-bfcm-klaviyo]');
          this.formId = this.dataset.klaviyoForm || '';
          this.timeout = parseInt(this.dataset.timeout || '8000', 10);
          this.email = this.form.querySelector('input[type="email"]');
          this.consent = this.form.querySelector('input[type="checkbox"]');
          this.phoneWrap = this.form.querySelector('[data-bfcm-phone]');
          this.phone = this.phoneWrap?.querySelector('input');
          this.error = this.querySelector('[data-bfcm-error]');
          this.pending = false;

          this.onSubmit = this.onSubmit.bind(this);
          this.onKlaviyo = this.onKlaviyo.bind(this);
          this.togglePhone = this.togglePhone.bind(this);

          this.form.addEventListener('submit', this.onSubmit);
          this.consent?.addEventListener('change', this.togglePhone);
          window.addEventListener('klaviyoForms', this.onKlaviyo);
          this.togglePhone();
        }

        disconnectedCallback() {
          window.removeEventListener('klaviyoForms', this.onKlaviyo);
          clearTimeout(this.timer);
        }

        /* The phone field is only shown when the visitor opts in to texts */
        togglePhone() {
          if (!this.phoneWrap) return;
          const wanted = !!this.consent?.checked;
          this.phoneWrap.hidden = !wanted;
          if (this.phone) this.phone.required = wanted;
        }

        klaviyoForm() {
          return (
            this.host?.querySelector('form') ||
            (this.formId ? document.querySelector(`.klaviyo-form-${this.formId} form`) : null)
          );
        }

        waitForKlaviyoForm(ms) {
          return new Promise((resolve) => {
            const started = Date.now();
            const check = () => {
              const form = this.klaviyoForm();
              if (form) return resolve(form);
              if (Date.now() - started > ms) return resolve(null);
              setTimeout(check, 150);
            };
            check();
          });
        }

        showError(message) {
          if (!this.error) return;
          this.error.textContent = message || '';
          this.error.hidden = !message;
        }

        async onSubmit(event) {
          event.preventDefault();
          if (this.pending) return;

          const email = (this.email?.value || '').trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            this.showError(this.dataset.errorEmail || 'Please enter a valid e-mail address.');
            this.email?.focus();
            return;
          }

          const wantsSms = !!this.consent?.checked;
          const phone = (this.phone?.value || '').trim();
          if (wantsSms && this.phone && !phone) {
            this.showError(this.dataset.errorPhone || 'Please enter your phone number.');
            this.phone.focus();
            return;
          }

          this.showError('');
          this.classList.add('is-loading');

          const klaviyoForm = await this.waitForKlaviyoForm(3000);
          const klaviyoEmail = klaviyoForm?.querySelector('input[type="email"], input[name="email"], input[autocomplete="email"]');
          if (!klaviyoForm || !klaviyoEmail) {
            this.fallback();
            return;
          }

          setNativeValue(klaviyoEmail, email);

          const klaviyoPhone = klaviyoForm.querySelector('input[type="tel"], input[name="phone_number"]');
          if (wantsSms && phone && klaviyoPhone) setNativeValue(klaviyoPhone, phone);

          const klaviyoConsent = klaviyoForm.querySelector('input[type="checkbox"]');
          if (klaviyoConsent && klaviyoConsent.checked !== wantsSms) klaviyoConsent.click();

          this.pending = true;
          this.timer = setTimeout(() => {
            if (this.pending) this.fallback();
          }, this.timeout);

          const submit = klaviyoForm.querySelector('button[type="submit"], button');
          if (submit) submit.click();
          else klaviyoForm.requestSubmit?.();
        }

        onKlaviyo(event) {
          if (!this.pending) return;

          const detail = event.detail || {};
          if (detail.formId && this.formId && detail.formId !== this.formId) return;
          if (detail.type === 'submit' || detail.type === 'stepSubmit' || detail.type === 'redirectedToUrl') this.success();
        }

        success() {
          this.pending = false;
          clearTimeout(this.timer);
          this.classList.remove('is-loading');
          this.classList.add('is-done');
          this.querySelector('[data-bfcm-success]')?.focus();
        }

        fallback() {
          this.pending = false;
          clearTimeout(this.timer);
          this.classList.remove('is-loading');
          this.showError(this.dataset.errorFallback || 'Something went wrong. Please try again.');

          /* Show the real Klaviyo form when it did render, so the visitor can finish signing up there */
          if (this.klaviyoForm()) this.classList.add('is-fallback');
        }
      }
    );
  }
})();
